import { milliseconds } from 'date-fns';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactModal from 'react-modal';
import { useInterval } from '../../../hooks/useInterval';
import { EXPIRED_TIME_KEY } from '../../../utils/IdleTimeout';

const POPUP_DELAY_BEFORE_LOGOUT = { seconds: 30 };

type ModalProps = {
  delay: Duration;
};

function Modal({ delay }: ModalProps) {
  const [timeout, setTimeout] = useState(milliseconds(delay) / 1000);

  useInterval(() => {
    setTimeout(timeout => timeout - 1);
  }, 1000);

  return (
    <div className="overflow-hidden rounded-3xl bg-white font-dh text-dh-ink shadow-2xl">
      <header className="flex items-center bg-gradient-to-r from-dh-plum to-dh-magenta px-6 py-4">
        <h4 className="m-0 flex grow text-3xl font-light leading-tight text-white">
          Logout
        </h4>
      </header>

      <section className="flex flex-col items-center px-6 py-8 text-center">
        <h2 className="mb-4 text-2xl font-light">Session timeout warning</h2>
        <p className="text-xl">
          You will be automatically logged out in{' '}
          <span className="font-medium text-dh-magenta">{timeout}</span>{' '}
          seconds.
        </p>
        <p className="mt-4 text-lg text-gray-500">
          Move your mouse to continue your session and close this message.
        </p>
      </section>
    </div>
  );
}

function useShouldDisplay() {
  const [shouldDisplay, setShouldDisplay] = useState(false);

  useInterval(() => {
    if (process.env.NODE_ENV === 'development') {
      return;
    }

    const expiredTime = parseInt(
      localStorage.getItem(EXPIRED_TIME_KEY) || '0',
      10
    );

    setShouldDisplay(
      expiredTime - milliseconds(POPUP_DELAY_BEFORE_LOGOUT) < Date.now()
    );
  }, 200);

  return shouldDisplay;
}

export function AutoLogoutModal() {
  const { t } = useTranslation();

  const shouldDisplay = useShouldDisplay();

  return (
    <ReactModal
      id="auto-logout-modal"
      isOpen={shouldDisplay}
      contentLabel={t('Header:Logout')}
      className="relative w-11/12 max-w-lg max-h-full outline-none"
      overlayClassName="fixed top-0 left-0 right-0 bottom-0 z-50 flex items-start justify-center bg-black/60 py-16 backdrop-blur-sm"
      style={{
        overlay: {
          zIndex: 9999,
        },
      }}
    >
      <Modal delay={POPUP_DELAY_BEFORE_LOGOUT} />
    </ReactModal>
  );
}
